import { useEffect, useRef, useState, type ReactElement, type ReactNode, isValidElement } from 'react';
import { cn } from '../../lib/utils';

function codeChildrenToString(children: ReactNode): string {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(codeChildrenToString).join('');
  if (isValidElement(children)) {
    const p = children.props as { children?: ReactNode };
    return codeChildrenToString(p.children);
  }
  return '';
}

interface MermaidBlockProps {
  source: string;
  className?: string;
}

/**
 * Renders a Mermaid diagram from markdown ```mermaid ... ``` blocks.
 */
export function MermaidBlock({ source, className }: MermaidBlockProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let cancelled = false;
    setError(null);

    void (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        const dark = document.documentElement.classList.contains('dark');
        mermaid.initialize({
          startOnLoad: false,
          theme: dark ? 'dark' : 'default',
          securityLevel: 'strict',
        });
        wrap.replaceChildren();
        const graphEl = document.createElement('div');
        graphEl.className = 'mermaid';
        graphEl.textContent = source.trim();
        wrap.appendChild(graphEl);
        await mermaid.run({ nodes: [graphEl] });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return (
      <pre
        className={cn(
          'my-3 overflow-x-auto rounded-lg border border-amber-200 bg-amber-50 p-3 font-mono text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-100',
          className
        )}
      >
        {`Mermaid: ${error}\n\n${source}`}
      </pre>
    );
  }

  return <div ref={wrapRef} className={cn('my-4 flex justify-center overflow-x-auto', className)} />;
}

export function isMermaidCodeElement(
  node: unknown
): node is ReactElement<{ className?: string; children?: ReactNode }> {
  if (!isValidElement(node)) return false;
  const el = node as ReactElement<{ className?: string }>;
  if (el.type !== 'code') return false;
  const cls = el.props?.className || '';
  return /\blanguage-mermaid\b/.test(cls);
}

export function mermaidSourceFromCodeElement(el: ReactElement<{ children?: ReactNode }>): string {
  return codeChildrenToString(el.props.children).replace(/\n$/, '');
}
